import React, { useState } from 'react';
import ActionButtons from '../../common/table/ActionButtons';
import { IWorkshopType } from '../../../interface/Workshop';
import { ColumnDef } from '@tanstack/react-table';
import { Table } from '../../common/table/Table';
import { useFetchWorkshopTypes } from '../../../hooks/workshop/useFetchWorkshopTypes';
import { useDeleteWorkshopType } from '../../../hooks/workshop/useDeleteWorkshopType';
import { useDebounce } from 'use-debounce';
import Pagination from '../../common/table/Pagination';
import Search from '../../common/Search';
import MobilePagination from '../../common/table/MobilePagination';
import ConfirmationModal from '../../modals/ConfirmationModal';
import { getUserData } from '../../../utils/utils';
import { createWorkshopType, updateWorkshopType } from '../../../apis/workshopTypeApi';
import { toast } from 'react-toastify';

const emptyForm: IWorkshopType = {
    workshopTypeID: 0,
    workshopTypeName: '',
};

const WorkshopTable: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Add/Edit modal state
    const [showFormModal, setShowFormModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<IWorkshopType>(emptyForm);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');

    // Delete confirmation state
    const [confirmationProps, setConfirmationProps] = useState({
        message: '',
        onConfirm: () => { },
        onCancel: () => { },
    });
    const [confirmationOpen, setConfirmationOpen] = useState(false);

    const { data: workshopTypes, isLoading, isError, error, refetch } =
        useFetchWorkshopTypes(debouncedSearchQuery, currentPage, itemsPerPage);
    const { deleteWorkshopTypeData, loading: deleteLoader } = useDeleteWorkshopType();
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const totalPages = workshopTypes ? Math.ceil(workshopTypes.totalItems / itemsPerPage) : 1;
    const userData = getUserData();
    const canEdit = userData.role !== 'Scope.AdminView' &&
        userData.role !== 'Scope.StudentService' &&
        userData.role !== 'Scope.PayrollService1' &&
        userData.role !== 'Scope.PayrollService2' &&
        userData.role !== 'Scope.StudentService_PayrollService1' &&
        userData.role !== 'Scope.StudentService_PayrollService1AND2';

    const columns: ColumnDef<IWorkshopType>[] = [
        {
            accessorKey: 'workshopTypeID',
            header: 'ID',
        },
        {
            accessorKey: 'workshopTypeName',
            header: 'WORKSHOP TYPE NAME',
        },
        {
            id: 'actions',
            header: 'ACTION',
            cell: ({ row }) => (
                <ActionButtons
                    id={row.original.workshopTypeID}
                    onEdit={() => openEditModal(row.original)}
                    onDelete={() => showDeleteConfirmation(row.original.workshopTypeID)}
                    loader={deleteLoader && deletingId === row.original.workshopTypeID}
                />
            ),
        },
    ];

    const openCreateModal = () => {
        setFormData(emptyForm);
        setFormError('');
        setIsEditing(false);
        setShowFormModal(true);
    };

    const openEditModal = (workshopType: IWorkshopType) => {
        setFormData({ ...workshopType });
        setFormError('');
        setIsEditing(true);
        setShowFormModal(true);
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.workshopTypeName.trim()) {
            setFormError('Workshop Type Name is required.');
            return;
        }
        setFormLoading(true);
        setFormError('');
        try {
            if (isEditing) {
                await updateWorkshopType(formData.workshopTypeID, formData);
            } else {
                await createWorkshopType({ workshopTypeName: formData.workshopTypeName });
            }
            refetch();
            setShowFormModal(false);
            setFormData(emptyForm);
        } catch {
            setFormError('An error occurred. Please try again.');
        } finally {
            setFormLoading(false);
        }
    };

    const showDeleteConfirmation = (rowId: number) => {
        setConfirmationProps({
            message: 'Are you sure you want to delete this Workshop Type?',
            onConfirm: async () => {
                setDeletingId(rowId);
                await deleteWorkshopTypeData(rowId, refetch);
                setDeletingId(null);
                setConfirmationOpen(false);
            },
            onCancel: () => {
                setConfirmationOpen(false);
            },
        });
        setConfirmationOpen(true);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    if (isLoading) return <p>Loading...</p>;
    if (isError) return <p>{error.message}</p>;

    return (
        <>
            {/* Add/Edit Modal */}
            {showFormModal && (
                <div
                    className="modal fade show d-block"
                    tabIndex={-1}
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowFormModal(false); }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg" style={{ width: "750px" }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {isEditing ? 'Edit Workshop Type' : 'Add Workshop Type'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowFormModal(false)}
                                    aria-label="Close"
                                />
                            </div>
                            <form onSubmit={handleFormSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label htmlFor="workshopTypeName" className="form-label fw-semibold">
                                            Workshop Type Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="workshopTypeName"
                                            type="text"
                                            className={`form-control ${formError ? 'is-invalid' : ''}`}
                                            name="workshopTypeName"
                                            value={formData.workshopTypeName}
                                            onChange={handleFormChange}
                                            placeholder="Enter workshop type name"
                                            autoFocus
                                        />
                                        {formError && (
                                            <div className="invalid-feedback">{formError}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowFormModal(false)}
                                        disabled={formLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={formLoading}
                                    >
                                        {formLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                                Saving...
                                            </>
                                        ) : (
                                            isEditing ? 'Update' : 'Create'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <div className="seachStrip pb-lg-4 pb-md-3 pb-3">
                <div className="row g-4 flex-lg-row flex-md-row flex-sm-row flex-column">
                    <div className="col-12 d-flex align-items-center flex-lg-nowrap flex-md-nowrap flex-sm-nowrap flex-wrap justify-content-between gap-lg-3 gap-md-3 gap-sm-3 gap-4">
                        <div className='DistrictpageSubTitle'>
                            <h2 className='d-flex m-0 align-items-center text-nowrap gap-3'>
                                <b>Workshop Type</b>
                            </h2>
                        </div>
                        {canEdit && (
                            <button className="btn btn-primary d-flex align-items-center gap-2" onClick={openCreateModal}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M19 11H13V5C13 4.73478 12.8946 4.48043 12.7071 4.29289C12.5196 4.10536 12.2652 4 12 4C11.7348 4 11.4804 4.10536 11.2929 4.29289C11.1054 4.48043 11 4.73478 11 5V11H5C4.73478 11 4.48043 11.1054 4.29289 11.2929C4.10536 11.4804 4 11.7348 4 12C4 12.2652 4.10536 12.5196 4.29289 12.7071C4.48043 12.8946 4.73478 13 5 13H11V19C11 19.2652 11.1054 19.5196 11.2929 19.7071C11.4804 19.8946 11.7348 20 12 20C12.2652 20 12.5196 19.8946 12.7071 19.7071C12.8946 19.5196 13 19.2652 13 19V13H19C19.2652 13 19.5196 12.8946 19.7071 12.7071C19.8946 12.5196 20 12.2652 20 12C20 11.7348 19.8946 11.4804 19.7071 11.2929C19.5196 11.1054 19.2652 11 19 11Z"
                                        fill="white" />
                                </svg>
                                New
                            </button>
                        )}
                    </div>

                    <div className="col-12 d-flex align-items-center justify-content-end gap-2">
                        <div className="d-flex w-100 flex-lg-nowrap flex-md-nowrap flex-wrap align-items-center justify-content-between gap-3">
                            <div className="sitesDetails">
                                <ul className="tableInfo flex-lg-row flex-md-row flex-sm-row flex-column d-flex justify-content-around align-items-lg-center align-items-md-center align-items-sm-center align-items-start gap-2">
                                    <li className="d-flex align-items-center gap-2">
                                        <span>Total</span>
                                        <span>:</span>
                                        <div className="totalCount bg-none shadow-none">
                                            {workshopTypes?.totalItems || 0}
                                        </div>
                                    </li>
                                </ul>
                            </div>

                            <Search
                                searchQuery={searchQuery}
                                handleSearchChange={handleSearchChange}
                                setSearchQuery={setSearchQuery}
                            />

                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                totalPages={totalPages}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="paginationDiv h-100 pt-3 mb-lg-0 mb-md-0 mb-0">
                <div className="row g-4 flex-lg-column flex-md-column flex-column-reverse h-100">
                    <div className="col-12 h-100">
                        <div className="pageTable h-100">
                            <div className="pageTableInner personalTab">
                                <Table data={workshopTypes?.data || []} columns={columns} />
                            </div>
                            <MobilePagination
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                totalPages={totalPages}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isOpen={confirmationOpen}
                message={confirmationProps.message}
                onConfirm={confirmationProps.onConfirm}
                onCancel={confirmationProps.onCancel}
                title="Delete Workshop Type"
            />
        </>
    );
};

export default WorkshopTable;